import {
  Add,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Paper,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import api from "../../services/api";
import CreateSprintDialog from "../dialogs/SprintDialog";
import CreateSprintGoalDialog from "../dialogs/SprintGoalDialog";
import { useWorkspace } from "../../utils/WorkspaceContext";
import useDebounce from "../../hooks/debounceHook";

const StyledPaper = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(0.1),
  padding: theme.spacing(0.1),
}));

const TrackHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1),
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const SprintHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1),
  cursor: "pointer",
  marginLeft: theme.spacing(3),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const GoalRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "0.3fr 7fr 2fr 2fr 2fr 0fr",
  gap: theme.spacing(2),
  alignItems: "center",
  padding: theme.spacing(1),
  marginLeft: theme.spacing(6),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "0fr 7fr 2fr 2fr 2fr 0fr",
  gap: theme.spacing(2),
  padding: theme.spacing(1),
  marginLeft: theme.spacing(6),
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  fontWeight: "bold",
}));

const ColorBar = styled(Box)(({ color }) => ({
  width: "4px",
  height: "100%",
  backgroundColor: color,
  borderRadius: "2px",
  marginRight: "8px",
}));

const SprintListView = () => {
  const [tracks, setTracks] = useState([]);
  const [expandedTracks, setExpandedTracks] = useState({});
  const [expandedSprints, setExpandedSprints] = useState({});
  const [openSprintDialog, setOpenSprintDialog] = useState(false);
  const [openSprintGoalDialog, setOpenSprintGoalDialog] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [selectedSprint, setSelectedSprint] = useState(null);
  const { currentWorkspace } = useWorkspace();

  const debouncedUpdateSprint = useDebounce(async (sprintId, updates) => {
    try {
      await api.patch(`sprints/${sprintId}/`, {
        ...updates,
        workspace: currentWorkspace.id,
      });
      fetchTracks();
    } catch (error) {
      console.error("Error updating sprint:", error);
    }
  }, 1000);

  const fetchTracks = async () => {
    try {
      const response = await api.get("grouped-sprints/", {params: {workspace_id: currentWorkspace.id}});
      
      setTracks(response.data.payload);
    } catch (error) {
      console.error("Error fetching sprints:", error);
    }
  };

  useEffect(() => {
    fetchTracks();
  }, []);

  const toggleTrack = (trackId) => {
    setExpandedTracks((prev) => ({
      ...prev,
      [trackId]: !prev[trackId],
    }));
  };

  const toggleSprint = (sprintId) => {
    setExpandedSprints((prev) => ({
      ...prev,
      [sprintId]: !prev[sprintId],
    }));
  };

  const handleCreateSprint = () => {
    setOpenSprintDialog(true);
  };

  const handleCreateSprintGoal = () => {
    setOpenSprintGoalDialog(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box />

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleCreateSprint()}
        >
          Create Sprint
        </Button>

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleCreateSprintGoal()}
        >
          Create Sprint Goal
        </Button>
      </Stack>

      <StyledPaper elevation={1}>
        {tracks.map((track) => (
          <Box key={track.id}>
            <TrackHeader onClick={() => toggleTrack(track.id)}>
              {expandedTracks[track.id] ? (
                <KeyboardArrowDown />
              ) : (
                <KeyboardArrowRight />
              )}
              <Typography variant="h6">{track.name}</Typography>
            </TrackHeader>

            {expandedTracks[track.id] && (
              <>
                {track.sprints?.map((sprint) => (
                  <Box key={sprint.id}>
                    <SprintHeader onClick={() => toggleSprint(sprint.id)}>
                      {expandedSprints[sprint.id] ? (
                        <KeyboardArrowDown />
                      ) : (
                        <KeyboardArrowRight />
                      )}
                      <Typography variant="h6">{`${sprint.name} (${sprint.start || '??'} - ${sprint.end || '??'})`}</Typography>
                    </SprintHeader>

                    {expandedSprints[sprint.id] && (
                      <>
                        <HeaderRow>
                          <Box />
                          <Typography>Status</Typography>
                          <Typography>Goal</Typography>
                        </HeaderRow>

                        {sprint.sprint_goals?.map((goal) => (
                          <GoalRow key={goal.id}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <ColorBar color="#6B7280" />
                            </Box>
                            <Typography>{goal.is_completed ? "Complete": "In-Complete"}</Typography>
                            <Typography>{goal.goal}</Typography>
                          </GoalRow>
                        ))}
                      </>
                    )}
                  </Box>
                ))}
              </>
            )}
          </Box>
        ))}
      </StyledPaper>

      <CreateSprintDialog
        open={openSprintDialog}
        onClose={() => setOpenSprintDialog(false)}
        onSuccess={() => {
          setOpenSprintDialog(false);
          fetchTracks();
        }}
      />

      <CreateSprintGoalDialog
        open={openSprintGoalDialog}
        onClose={() => setOpenSprintGoalDialog(false)}
        onSuccess={() => {
          setOpenSprintGoalDialog(false);
          fetchTracks();
        }}
      />
    </Box>
  );
};

export default SprintListView;
